import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Target, ListChecks, Flag, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import PageHeader from "@/components/PageHeader";
import { IslamicPatternCorner } from "@/components/IslamicPattern";

const VisiMisi = () => {
  const [vm, setVm] = useState({});
  useSeo("Visi & Misi", "Visi, misi, tujuan, dan nilai-nilai pendidikan Madrasah Ibtidaiyah Futuhiyyah Doro.");
  useEffect(() => { api.get("/vision-mission").then((r) => setVm(r.data)).catch(() => {}); }, []);

  return (
    <div>
      <PageHeader title="Visi & Misi" subtitle="Arah, tujuan, dan nilai-nilai yang menjadi landasan pendidikan di MI Futuhiyyah." crumbs={[{ label: "Visi & Misi" }]} />
      <div className="mx-auto max-w-5xl px-6 py-14 space-y-14">
        {/* VISI */}
        <motion.section initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-700 via-emerald-800 to-emerald-950 p-10 text-center shadow-lg">
          <IslamicPatternCorner className="pointer-events-none absolute -right-10 -top-10 h-56 w-56 text-white/10" />
          <div className="relative">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/20"><Target className="h-7 w-7 text-emerald-200" /></div>
            <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-emerald-200">Visi</p>
            <p className="mx-auto mt-3 max-w-2xl font-heading text-xl font-semibold leading-relaxed text-white sm:text-2xl">"{vm.vision}"</p>
          </div>
        </motion.section>

        {/* MISI */}
        <section>
          <div className="mb-6 flex items-center gap-3">
            <ListChecks className="h-6 w-6 text-emerald-600" />
            <h2 className="font-heading text-2xl font-bold text-slate-900">Misi</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            {(vm.missions || []).map((m, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.3, delay: i * 0.05 }}
                className="flex gap-4 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-600 font-heading text-sm font-bold text-white">{i + 1}</span>
                <p className="text-sm leading-relaxed text-slate-700">{m}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* TUJUAN */}
        {vm.goals?.length > 0 && (
          <section className="rounded-3xl bg-slate-50 p-8">
            <div className="mb-6 flex items-center gap-3">
              <Flag className="h-6 w-6 text-emerald-600" />
              <h2 className="font-heading text-2xl font-bold text-slate-900">Tujuan Sekolah</h2>
            </div>
            <ul className="space-y-3">
              {vm.goals.map((g, i) => (
                <li key={i} className="flex gap-3 text-slate-700"><span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-emerald-500" />{g}</li>
              ))}
            </ul>
          </section>
        )}

        {/* NILAI */}
        {vm.values?.length > 0 && (
          <section>
            <div className="mb-6 flex items-center gap-3">
              <Sparkles className="h-6 w-6 text-emerald-600" />
              <h2 className="font-heading text-2xl font-bold text-slate-900">Nilai-Nilai Pendidikan</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              {vm.values.map((v, i) => (
                <span key={i} className="rounded-full border border-emerald-200 bg-emerald-50 px-5 py-2.5 text-sm font-medium text-emerald-800">{v}</span>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default VisiMisi;
